import styled, { css, keyframes } from 'styled-components';

/* ── Page wrapper ── */
export const PageWrapper = styled.div`
  padding: 2rem;
  min-height: calc(100vh - var(--header-height));
  background: #fafafa;

  @media (max-width: 40rem) {
    padding: 1rem;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e8e8e8;

  > a:first-child {
    background: none;
    display: flex;
    align-items: center;
    color: #323232;
    text-align: center;
    margin-bottom: 1rem;
    font-family: 'RedhatDisplay-Bold';
    font-size: 1.1em;
    width: fit-content;
    transition: opacity 0.3s;
    opacity: 0.8;

    svg {
      margin-right: 0.4rem;
    }

    &:hover {
      opacity: 1;

      > svg {
        opacity: 1;
      }
    }
  }

  > div {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
`;

export const PageTitle = styled.h1`
  font-family: 'RedHatDisplay-Bold';
  font-size: 1.6rem;
  color: #323232;
  margin: 0;

  @media (max-width: 40rem) {
    font-size: 1.3rem;
  }
`;

/* ── Skeleton ── */
const shimmer = keyframes`
  0%   { background-position: -400% 0; }
  100% { background-position:  400% 0; }
`;

export const SkeletonBlock = styled.div<{
  $w?: string;
  $h?: string;
  $radius?: string;
}>`
  width: ${p => p.$w ?? '100%'};
  height: ${p => p.$h ?? '1rem'};
  border-radius: ${p => p.$radius ?? '0.4rem'};
  background: linear-gradient(90deg, #ebebeb 25%, #d8d8d8 50%, #ebebeb 75%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  display: inline-block;
`;

/* ── Config card ── */
export const ConfigCard = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e2e2e2;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

export const CardTitle = styled.h2`
  font-family: 'RedHatDisplay-Bold';
  font-size: 1.25rem;
  color: #323232;
  margin: 0 0 1.25rem 0;
  min-height: 1.5rem;

  @media (max-width: 40rem) {
    font-size: 1.05rem;
  }
`;

export const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 48rem) {
    grid-template-columns: 1fr;
  }
`;

export const ConfigLeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-right: 1px solid #e2e2e2;
  padding-right: 1.5rem;

  @media (max-width: 48rem) {
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #e2e2e2;
    padding-bottom: 1.5rem;
  }
`;

export const ConfigRightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FieldLabel = styled.span`
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.82rem;
  color: #888;
  display: block;
  margin-bottom: 0.35rem;
`;

export const InlineFieldGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledSelect = styled.select`
  height: 2.6rem;
  border: 1px solid #e2e2e2;
  border-radius: 0.5rem;
  padding: 0 0.75rem;
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.9rem;
  color: #323232;
  background: #fff;
  outline: none;
  cursor: pointer;
  min-width: 6rem;
  transition: border-color 0.2s;

  &:focus {
    border-color: #ff9800;
  }

  &:disabled {
    background: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

export const StyledInput = styled.input`
  height: 2.6rem;
  border: 1px solid #e2e2e2;
  border-radius: 0.5rem;
  padding: 0 0.75rem;
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.9rem;
  color: #323232;
  background: #fff;
  outline: none;
  width: 100%;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #ff9800;
  }

  &::placeholder {
    color: #bbb;
  }

  &:disabled {
    background: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

export const DurationInput = styled(StyledInput)`
  width: 4.5rem;
`;

export const InfoSection = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const InfoTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const InfoTag = styled.span`
  background: #f0f0f0;
  color: #323232;
  padding: 0.25rem 0.6rem;
  border-radius: 0.4rem;
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.8rem;
  border: 1px solid #e2e2e2;
`;

/* ── Intervals ── */
export const IntervalRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 0.75rem;
  align-items: end;

  @media (max-width: 30rem) {
    grid-template-columns: 1fr;
  }
`;

export const IntervalDivider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 0.25rem 0;
`;

/* ── Card footer ── */
export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1.2rem;
  margin-top: 1rem;
  border-top: 1px solid #f0f0f0;

  .secondary-button,
  .primary-button {
    height: 2.6rem;
    border-radius: 0.5rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 0.9rem;
    padding: 0 1.4rem;
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }

  .secondary-button {
    &:hover {
      svg {
        stroke: #4caf50;
      }
    }
  }

  @media (max-width: 30rem) {
    flex-direction: column-reverse;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

export const ScrollHint = styled.p`
  display: none;

  @media (max-width: 40rem) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.75rem;
    color: #9ca3af;
    margin: 0 0 0.5rem;
  }
`;

/* ── Schedule table ── */
export const TableWrapper = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e2e2e2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

export const TableScroller = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const ScheduleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 48.75rem;
`;

export const TableHead = styled.thead`
  background: #fafafa;
  border-bottom: 2px solid #ebebeb;

  @media (max-width: 40rem) {
    th:first-child {
      position: sticky;
      left: 0;
      z-index: 3;
      background: #fafafa;
      box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
    }
  }
`;

export const Th = styled.th`
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.82rem;
  color: #6b7280;
  padding: 0.85rem 0.75rem;
  text-align: left;
  white-space: nowrap;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const slideDown = keyframes`
  from { opacity: 0.5; transform: translateY(-6px); }
  to   { opacity: 1;   transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0.5; transform: translateY(6px); }
  to   { opacity: 1;   transform: translateY(0); }
`;

export const TableRow = styled.tr<{ $animDir?: 'up' | 'down' | null }>`
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 152, 0, 0.03);
  }

  @media (max-width: 40rem) {
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background: #fff;
      box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
    }
  }

  ${p =>
    p.$animDir === 'down' &&
    css`
      animation: ${slideDown} 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    `}

  ${p =>
    p.$animDir === 'up' &&
    css`
      animation: ${slideUp} 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    `}
`;

export const ModalityGroupRow = styled.tr`
  background: #fff7ec;
  border-top: 1px solid #ffe2bd;
  border-bottom: 1px solid #ffe2bd;
`;

export const ModalityGroupCell = styled.td`
  padding: 0.6rem 0.75rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.8rem;
  color: #d97706;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const BreakRow = styled.tr`
  background: #f8f9fa;
  border-bottom: 1px solid #ebebeb;

  &:last-child {
    border-bottom: none;
  }
`;

export const BreakCell = styled.td`
  padding: 0.6rem 0.75rem;
  color: #9ca3af;
  font-family: 'RedHatDisplay-Regular';
  font-style: italic;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  text-align: center;
`;

export const Td = styled.td`
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.88rem;
  color: #323232;
  padding: 0.6rem 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TdTime = styled(Td)`
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.88rem;
  color: #555;
  text-align: center;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

export const ArenaSelect = styled.select`
  border: 1px solid #e2e2e2;
  border-radius: 0.4rem;
  padding: 0.3rem 0.4rem;
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.83rem;
  color: #323232;
  background: #fff;
  outline: none;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #ff9800;
  }
`;

/* ── Ordem cell ── */
export const OrdemCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
`;

export const OrdemNumber = styled.span`
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.85rem;
  color: #323232;
  min-width: 1.4rem;
  text-align: center;
`;

/* ── Modality config sections ── */
export const ModalitiesConfigSection = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const ModalityConfigBlock = styled.div`
  padding: 1rem;
  border: 1px solid #e8e8e8;
  border-radius: 0.6rem;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ModalityConfigTitle = styled.h3`
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.9rem;
  color: #323232;
  margin: 0;
`;

export const ModalityConfigDivider = styled.div`
  height: 1px;
  background: #ebebeb;
`;

export const ArenaTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const OrdemBtn = styled.button<{ $visible?: boolean }>`
  width: 1.4rem;
  height: 1.4rem;
  border: 1px solid #e2e2e2;
  border-radius: 0.3rem;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #888;
  flex-shrink: 0;
  transition:
    border-color 0.18s,
    color 0.18s,
    background 0.18s,
    transform 0.18s;
  opacity: ${p => (p.$visible === false ? 0 : 1)};
  pointer-events: ${p => (p.$visible === false ? 'none' : 'auto')};

  &:hover {
    border-color: #ff9800;
    color: #ff9800;
    background: rgba(255, 152, 0, 0.06);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;
